// src/components/ServiceDetailTemplate.jsx
import React, { useEffect, useRef } from "react";
import PageHeader from "./PageHeader"; // adjust path if your project differs
import { CheckCircle2 } from "lucide-react";

export default function ServiceDetailTemplate({ title, subtitle, features = [], children }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let particles = [];
    let raf = null;
    const init = () => {
      const w = (canvas.width = canvas.offsetWidth);
      const h = (canvas.height = canvas.offsetHeight);
      particles = [];
      const count = Math.max(6, Math.round((w * h) / 60000));
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          r: 0.8 + Math.random() * 2.6,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          a: 0.15 + Math.random() * 0.5,
        });
      }
    };

    const draw = () => {
      const w = canvas.width;
      const h = canvas.height;
      ctx.clearRect(0, 0, w, h);
      for (let p of particles) {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < -10) p.x = w + 10;
        if (p.x > w + 10) p.x = -10;
        if (p.y < -10) p.y = h + 10;
        if (p.y > h + 10) p.y = -10;
        ctx.beginPath();
        ctx.fillStyle = `rgba(0,196,204, ${p.a})`;
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fill();
      }
      raf = requestAnimationFrame(draw);
    };

    init();
    draw();
    const onResize = () => {
      init();
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return (
    <>
      <PageHeader title={title} subtitle={subtitle} />

      <div className="relative overflow-hidden">
        {/* top wave */}
        <div className="absolute inset-x-0 -top-20 pointer-events-none opacity-40">
          <svg viewBox="0 0 1440 200" preserveAspectRatio="none" className="w-full h-48">
            <defs>
              <linearGradient id="gd" x1="0" x2="1">
                <stop offset="0%" stopColor="#00C4CC" stopOpacity="0.14" />
                <stop offset="50%" stopColor="#007BFF" stopOpacity="0.10" />
                <stop offset="100%" stopColor="#00C4CC" stopOpacity="0.06" />
              </linearGradient>
            </defs>
            <path d="M0,160 C240,220 360,40 720,120 C1080,200 1320,60 1440,120 L1440,0 L0,0 Z" fill="url(#gd)" />
          </svg>
        </div>

        {/* particles canvas */}
        <div className="absolute inset-0 pointer-events-none">
          <canvas ref={canvasRef} style={{ width: "100%", height: "100%" }} />
        </div>

        {/* content area */}
        <div className="relative max-w-4xl mx-auto px-6 py-16 space-y-8">
          <div className="bg-white shadow-md rounded-2xl p-8 border border-gray-100 relative overflow-hidden">
            {/* soft interior glow */}
            <div className="absolute inset-0 pointer-events-none" aria-hidden>
              <div className="w-72 h-72 bg-[#00C4CC] blur-3xl rounded-full absolute -top-10 -left-10 opacity-20" />
              <div className="w-72 h-72 bg-[#007BFF] blur-3xl rounded-full absolute -bottom-10 -right-10 opacity-14" />
            </div>

            <div className="relative z-10">
              <h1 className="text-3xl font-bold text-[#0A1E3F] mb-3">{title}</h1>
              <p className="text-gray-700 leading-7">{subtitle}</p>

              {children}

              {/* features */}
              {features.length > 0 && (
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {features.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-3 text-gray-700">
                      <CheckCircle2 className="text-[#00C4CC] mt-1" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="relative bg-gradient-to-r from-[#06172b] to-[#071a33] text-white rounded-3xl p-10 overflow-hidden shadow-xl inline-block w-full">
              <svg className="absolute -bottom-6 left-0 w-full opacity-20" viewBox="0 0 1440 120" preserveAspectRatio="none">
                <path d="M0 48C120 88 360 80 720 56C1080 32 1320 16 1440 8V120H0z" fill="#00C4CC" />
              </svg>

              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute w-72 h-72 bg-[#00C4CC] rounded-full blur-3xl top-6 -left-16 animate-blob opacity-30" />
                <div className="absolute w-72 h-72 bg-[#007BFF] rounded-full blur-3xl bottom-6 -right-10 animate-blob animation-delay-2000 opacity-28" />
              </div>

              <div className="relative z-10">
                <h2 className="text-2xl font-semibold mb-2">Ready to see it in action?</h2>
                <p className="text-gray-200 mb-6">Request a free sample list or speak with our data specialists.</p>
                <div className="flex items-center justify-center gap-4">
                  <a href="/contact" className="bg-[#00C4CC] px-6 py-2 rounded-xl font-semibold shadow">Request Sample</a>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <style>{`
        .animate-blob { animation: blob 7s infinite ease-in-out; }
        .animation-delay-2000 { animation-delay: 2s; }
        @keyframes blob {
          0% { transform: translateY(0) scale(1); }
          33% { transform: translateY(-12px) scale(1.03); }
          66% { transform: translateY(8px) scale(0.98); }
          100% { transform: translateY(0) scale(1); }
        }
      `}</style>
    </>
  );
}
